import { AsyncPipe, NgClass } from '@angular/common';
import { Component, computed, ElementRef, signal, ViewChild } from '@angular/core';
import {
  IQBoostViewStore,
  IQChatFrom,
  ViewType,
  AiFeaturesService,
  IQChat,
} from '@maximizer/ai/data-access';
import { OpenAiService } from '@maximizer/core/shared/data-access';
import { DisposableComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { isNullOrEmpty, sanitizeMarkdownToHtml } from '@maximizer/shared/util';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import {
  KENDO_TEXTBOX,
  TextBoxComponent,
} from '@progress/kendo-angular-inputs';
import { catchError, filter, Observable, of, tap } from 'rxjs';
import {
  EventAction,
  EventJourney,
  IQBoostEventTracker,
} from '../classes/event-tracker';
import { IQBoostTextVersion } from '../classes/iqboost';
import { IqboostRecentUserPrompts } from '../classes/prompts/iqboost-recent-user-prompts';
import { ClickableRefPipe } from '../pipes/clickable-ref.pipe';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { EntityKeyRefPipe } from '../pipes/entity-key-ref.pipe';
import { MarkdownRenderingPipe } from '../pipes/markdown-rendering.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { WebSearchRefPipe } from '../pipes/web-search-ref.pipe';

/**
 * Conversations already cleared during this browser session.
 *
 * Deliberately in memory only and outside the component: chat is destroyed whenever the view
 * switches, and a page refresh must count as a first visit so the history is cleared again.
 */
const clearedConversations = new Set<string>();

@Component({
  selector: 'ai-iqboost-chat',
  templateUrl: './iqboost-chat.component.html',
  imports: [
    AsyncPipe,
    ClickableRefPipe,
    DeAnonymizerPipe,
    EntityKeyRefPipe,
    KENDO_TEXTBOX,
    KENDO_BUTTON,
    MarkdownRenderingPipe,
    NgClass,
    SafeHtmlPipe,
    WebSearchRefPipe,
    TranslatePipe,
    UI_KENDO,
    UI_PIPES,
  ],
})
export class IqboostChatComponent extends DisposableComponent {
  static readonly GeneralChatGroup = 'General';

  IQChatFrom = IQChatFrom;

  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  textVersion = computed(() => 
    this.aiFeatures.isSLE() 
      ? IQBoostTextVersion.defaultVersion 
      : IQBoostTextVersion.faVersion
  );

  promptIdentification = computed(() => {
    const promptName = this.store.currentPrompt()?.name ?? '';
    return isNullOrEmpty(promptName) ? IqboostChatComponent.GeneralChatGroup : promptName;
  });

  chatGroup = computed(() => {
    const promptName = this.promptIdentification();
    const chatGroupId = this.chatGroupId();

    let group = this.store.chatGroups()
      .find(group => group.id === chatGroupId);

    if (!group && promptName) {
      group = {
        id: chatGroupId,
        chatHistory: [],
      };
      this.store.chatGroups().push(group)
    }

    return group;
  });

  private readonly chatGroupId = computed(() => {
    const promptName = this.promptIdentification();
    return `${this.store.key()}|${promptName}`;
  });

  /** Conversation key for generate-response: entry + prompt + view. */
  private readonly conversationId = computed(() => {
    const key = this.store.key() ?? '';
    const promptName = this.store.currentPrompt()?.name.toLowerCase().trim() ?? '';
    return `${key}|${promptName}|${this.store.viewType()}`;
  });

  /** In-progress streamed answer HTML; a signal so out-of-zone chunks still re-render. */
  readonly streamingContent = signal<string | null>(null);

  constructor(
    public readonly store: IQBoostViewStore,
    public readonly recentUserPrompts: IqboostRecentUserPrompts,
    private readonly tracker: IQBoostEventTracker,
    private readonly aiFeatures: AiFeaturesService,
    private readonly openAi: OpenAiService,
  ) {
    super();

    this.addSubscription(
      this.store
        .streamingResponseFromAiSubject()
        .asObservable()
        .pipe(
          filter(() => this.store.viewType() === ViewType.Chat),
          tap((content) => this.renderStreamingChat(content)),
        )
        .subscribe(),
    );
    this.addSubscription(
      this.store
        .streamingResponseCompleteSubject()
        .asObservable()
        .pipe(
          filter(() => this.store.viewType() === ViewType.Chat),
          tap(() => this.commitStreamingToHistory()),
        )
        .subscribe(),
    );
    this.addSubscription(
      this.store
        .responseFromAiSubject()
        .asObservable()
        .pipe(
          filter(() => this.store.viewType() === ViewType.Chat),
          tap((aiResponseText) => {
            // Non-streaming final answer (streaming never emits on this subject).
            this.chatGroup()?.chatHistory.push({
              content: sanitizeMarkdownToHtml(aiResponseText),
              from: IQChatFrom.Ai,
            });
            this.scrollToBottom();
          }),
        )
        .subscribe(),
    );
    // The outside question waits for the clear so the first turn cannot race ahead of it.
    this.addSubscription(
      this.clearHistoryOnFirstVisit().subscribe(() => {
        const outsideQuestion = this.store.popOutsideQuestion();
        if (outsideQuestion) {
          this.triggerQuestionInternal(outsideQuestion);
        }
      }),
    );
  }

  /**
   * Wipes whatever the server still holds for this conversation the first time this browser
   * session opens it — a returning user would otherwise inherit history they cannot see or clear.
   * Re-entering the same chat later in the session keeps its context.
   */
  private clearHistoryOnFirstVisit(): Observable<unknown> {
    const conversationId = this.conversationId();

    if (isNullOrEmpty(this.store.key()) || clearedConversations.has(conversationId)) {
      return of(undefined);
    }

    return this.openAi.agentsConversationClearHistory(conversationId).pipe(
      tap(() => clearedConversations.add(conversationId)),
      // A failed clear must not block the chat; the next visit retries.
      catchError(() => of(undefined)),
    );
  }

  override ngOnDestroy(): void {
    // @case teardown destroys chat when leaving; persist the last streamed answer.
    this.commitStreamingToHistory();
    super.ngOnDestroy();
  }

  onBack(): void {
    this.store.setViewType(ViewType.Report);
  }

  triggerQuestion(txtMakeQuestion: TextBoxComponent): void {
    const question = txtMakeQuestion.value;
    this.triggerQuestionInternal(question);
    this.recentUserPrompts.pushUserPrompt(question);
    txtMakeQuestion.value = '';
  }

  private triggerQuestionInternal(question: string): void {
    if (this.appendUserChat(question)) {
      this.store.setLoading(true);
      this.store.triggerAskAiEvent({
        userPrompt: question,
        conversationId: this.conversationId(),
      });
      this.tracker.trackEvent(
        EventJourney.AskQuestion,
        EventAction.MessageSent,
        { prompt: this.store.currentPrompt()?.name ?? '' });
    }
  }

  private appendUserChat(content: string): boolean {
    content = content?.trim();
    if (isNullOrEmpty(content)) {
      return false;
    }
    // Commit any in-flight streamed answer before the new turn.
    this.commitStreamingToHistory();
    const chat: IQChat = { content, from: IQChatFrom.User };
    this.chatGroup()?.chatHistory.push(chat);
    setTimeout(() => this.scrollToBottom(), 0);
    return true;
  }

  private renderStreamingChat(content: string): void {
    this.streamingContent.set(sanitizeMarkdownToHtml(content));
    this.scrollToBottom();
  }

  /** Moves the live streamed answer into chat history; no-op when idle. */
  private commitStreamingToHistory(): void {
    const streaming = this.streamingContent();
    if (!streaming) {
      return;
    }
    this.chatGroup()?.chatHistory.push({ content: streaming, from: IQChatFrom.Ai });
    this.streamingContent.set(null);
  }

  private scrollToBottom(): void {
    const bottomPaddingOffset = 60;
    if (this.scrollContainer?.nativeElement) {
      this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight + bottomPaddingOffset;
    }
  }
}
